/**
 * WidgetPopup - Floating contact button with multiple options
 * Embeddable widget for external websites
 * IMPORTANT: Must have transparent background for iframe embedding
 */
import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, MessageSquare, Instagram, Facebook } from 'lucide-react';
import { EMBED_LANG } from '../i18n/embedLang';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [settings, setSettings] = useState(null);
  const [socialLinks, setSocialLinks] = useState({
    whatsapp: '',
    instagram: '',
    facebook: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    // Make body transparent for iframe embedding
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
  }, []);

  const loadSettings = async () => {
    try {
      // Load widget settings
      const widgetRes = await fetch(`${API}/widget/settings/public`);
      const widgetData = await widgetRes.json();
      setSettings(widgetData);

      // Load social links
      try {
        const socialRes = await fetch(`${API}/widget/social-links`);
        if (socialRes.ok) {
          const socialData = await socialRes.json();
          setSocialLinks(socialData);
        }
      } catch (e) {
        // Use defaults
      }
    } catch (err) {
      console.error('Widget load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = settings?.primary_color || '#C9A227';
  const position = settings?.position || 'bottom-right';
  const positionStyles = position === 'bottom-left' 
    ? { left: '20px', right: 'auto' } 
    : { right: '20px', left: 'auto' };

  // Contact options
  const contactOptions = [
    {
      id: 'chat',
      name: 'Live Chat',
      icon: MessageCircle,
      color: primaryColor,
      onClick: () => setShowChat(true),
      enabled: settings?.faq_enabled !== false
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: MessageSquare,
      color: '#25D366',
      // Echter Link statt window.open: window.open aus cross-origin iFrames
      // wird von Browsern geblockt (ERR_BLOCKED_BY_RESPONSE auf api.whatsapp.com)
      href: `https://wa.me/${(socialLinks.whatsapp || '41764449996').replace(/\D/g, '')}`,
      enabled: true
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: '#E4405F',
      href: `https://instagram.com/${socialLinks.instagram || 'strongfit_wetzikon'}`,
      enabled: true
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      href: `https://facebook.com/${socialLinks.facebook || 'strongfitwetzikon'}`,
      enabled: true
    }
  ];

  if (loading || !settings?.enabled) {
    return null;
  }

  // If chat is open, show the full chat interface
  if (showChat) {
    return (
      <div 
        className="fixed z-[9999]"
        style={{ bottom: '20px', ...positionStyles }}
      >
        <div 
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{ 
            width: '380px', 
            height: '550px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}
        >
          <iframe 
            src={`${BASE_URL}/widget/chat${EMBED_LANG !== 'de' ? `?lang=${EMBED_LANG}` : ''}`}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="StrongFit Chat"
          />
        </div>
        {/* Close button */}
        <button
          onClick={() => setShowChat(false)}
          className="absolute -top-2 -right-2 w-8 h-8 bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div 
      className="fixed z-[9999]"
      style={{ bottom: '20px', ...positionStyles }}
    >
      {/* Contact Options Menu */}
      {isOpen && (
        <div 
          className="mb-4 flex flex-col gap-3 animate-in slide-in-from-bottom-5 duration-200"
        >
          {contactOptions.filter(opt => opt.enabled).map((option, index) => {
            const inner = (
              <>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: option.color }}
                >
                  <option.icon className="w-5 h-5 text-white" />
                </div>
                <span className="font-medium text-gray-800 group-hover:text-gray-900">
                  {option.name}
                </span>
              </>
            );
            const commonProps = {
              className: "flex items-center gap-3 bg-white rounded-full py-3 px-5 shadow-lg hover:shadow-xl transition-all hover:scale-105 group",
              style: { 
                animationDelay: `${index * 50}ms`,
                animation: 'slideIn 0.3s ease-out forwards',
                opacity: 0,
                transform: 'translateY(10px)'
              },
              'data-testid': `widget-channel-${option.id}`
            };
            return option.href ? (
              <a key={option.id} href={option.href} target="_blank" rel="noopener noreferrer" {...commonProps}>
                {inner}
              </a>
            ) : (
              <button key={option.id} onClick={option.onClick} {...commonProps}>
                {inner}
              </button>
            );
          })}
        </div>
      )}

      {/* Main Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        style={{ 
          backgroundColor: primaryColor,
          boxShadow: `0 4px 20px ${primaryColor}50`
        }}
      >
        {isOpen ? (
          <X className="w-7 h-7 text-white transition-transform duration-300" />
        ) : (
          <MessageCircle className="w-7 h-7 text-white transition-transform duration-300" />
        )}
      </button>

      {/* Animation styles */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default WidgetPopup;
